/**
 * Lazy import helpers with chunk-load retry and hydration-safe preloading.
 *
 * What: Wraps dynamic imports for `React.lazy()` and exposes a preload hook that
 * can resolve synchronously once a module is cached.
 * Why: Prevents prerendered marketing routes from suspending into a fallback
 * during hydration while still recovering from stale chunk deploys.
 * When: Update when lazy route retry policy or preload semantics change.
 */
import React from 'react';

import { isChunkLoadError } from './chunkLoadError';

/**
 * Variance-escape constraint matching React's own `LazyExoticComponent<T extends ComponentType<any>>`.
 * `any` is load-bearing here — `ComponentType<unknown>` and `ComponentType<never>` reject
 * concrete component types (props are contravariant, and React classes carry `defaultProps`
 * typed `Partial<any>` which only `any` satisfies). Consolidated into one alias so the
 * exception is documented rather than scattered across every generic in the file.
 */

type AnyReactComponent = React.ComponentType<any>;

type LazyModule<T extends AnyReactComponent> = {
  default: T;
};

type LazyImportFn<T extends AnyReactComponent> = () => Promise<LazyModule<T>>;

type LoadStatus = 'uninitialized' | 'pending' | 'resolved' | 'rejected';

type LoadState<T extends AnyReactComponent> = {
  status: LoadStatus;
  promise: Promise<LazyModule<T>> | null;
  moduleObject: LazyModule<T> | null;
  error: unknown;
};

export type PreloadableLazyComponent<T extends AnyReactComponent> =
  React.LazyExoticComponent<T> & {
    preload: () => Promise<LazyModule<T>>;
  };

const loadStateByImportFn = new WeakMap<
  LazyImportFn<AnyReactComponent>,
  LoadState<AnyReactComponent>
>();

function getOrCreateLoadState<T extends AnyReactComponent>(
  importFn: LazyImportFn<T>
) {
  const existingState = loadStateByImportFn.get(
    importFn as LazyImportFn<AnyReactComponent>
  ) as LoadState<T> | undefined;

  if (existingState) {
    return existingState;
  }

  const initialState: LoadState<T> = {
    status: 'uninitialized',
    promise: null,
    moduleObject: null,
    error: null,
  };

  loadStateByImportFn.set(
    importFn as LazyImportFn<AnyReactComponent>,
    initialState as LoadState<AnyReactComponent>
  );

  return initialState;
}

function createResolvedThenable<T extends AnyReactComponent>(
  moduleObject: LazyModule<T>
): PromiseLike<LazyModule<T>> {
  return {
    then(onFulfilled) {
      if (typeof onFulfilled === 'function') {
        onFulfilled(moduleObject);
      }
      return undefined as never;
    },
  };
}

function createRejectedThenable(error: unknown): PromiseLike<never> {
  return {
    then(_onFulfilled, onRejected) {
      if (typeof onRejected === 'function') {
        onRejected(error);
      }
      return undefined as never;
    },
  };
}

function loadModuleWithRetry<T extends AnyReactComponent>(
  importFn: LazyImportFn<T>
) {
  const loadState = getOrCreateLoadState(importFn);

  if (loadState.status === 'resolved' && loadState.moduleObject) {
    return Promise.resolve(loadState.moduleObject);
  }

  if (loadState.status === 'rejected') {
    return Promise.reject(loadState.error);
  }

  if (loadState.status === 'pending' && loadState.promise) {
    return loadState.promise;
  }

  const loadPromise = importFn()
    .catch(error => {
      if (!isChunkLoadError(error)) {
        throw error;
      }

      return importFn().catch(retryError => {
        if (!isChunkLoadError(retryError)) {
          throw retryError;
        }

        if (typeof window !== 'undefined') {
          window.location.reload();
        }

        return new Promise<LazyModule<T>>(() => {});
      });
    })
    .then(
      moduleObject => {
        loadState.status = 'resolved';
        loadState.moduleObject = moduleObject;
        loadState.error = null;
        loadState.promise = Promise.resolve(moduleObject);
        return moduleObject;
      },
      error => {
        loadState.status = 'rejected';
        loadState.error = error;
        loadState.promise = null;
        throw error;
      }
    );

  loadState.status = 'pending';
  loadState.promise = loadPromise;
  loadState.error = null;

  return loadPromise;
}

function getLazyImportThenable<T extends AnyReactComponent>(
  importFn: LazyImportFn<T>
) {
  const loadState = getOrCreateLoadState(importFn);

  if (loadState.status === 'resolved' && loadState.moduleObject) {
    return createResolvedThenable(loadState.moduleObject);
  }

  if (loadState.status === 'rejected') {
    return createRejectedThenable(loadState.error);
  }

  return loadModuleWithRetry(importFn);
}

function preloadLazyImport<T extends AnyReactComponent>(
  importFn: LazyImportFn<T>
) {
  return loadModuleWithRetry(importFn);
}

/**
 * Wraps a dynamic import for use with React.lazy(). On chunk load failure, retries once;
 * if the retry fails, forces a full page reload so the user gets the latest assets.
 * Also exposes a `preload()` helper that can warm the same lazy payload used by
 * React during hydration.
 *
 * @param {() => Promise<{ default: React.ComponentType }>} importFn - Function that returns the dynamic import()
 * @returns {React.LazyExoticComponent<React.ComponentType>}
 */
export function lazyWithChunkRetry<T extends AnyReactComponent>(
  importFn: LazyImportFn<T>
) {
  const LazyComponent = React.lazy(
    () => getLazyImportThenable(importFn) as Promise<LazyModule<T>>
  ) as PreloadableLazyComponent<T>;

  LazyComponent.preload = () => preloadLazyImport(importFn);

  return LazyComponent;
}
